import { useState, useRef, useEffect, useCallback } from "react";
import { useSize, useDebounceFn } from "ahooks";
import { v4 } from "uuid";
import { InputNumber, Slider, SliderSingleProps, ConfigProvider } from "antd";
import { ReactComponent as CloseIcon } from "logic/src/components/icons/close.svg";
import { ReactComponent as CutIcon } from "logic/src/components/icons/cut.svg";
import { ReactComponent as LeftArrowIcon } from "logic/src/components/icons/leftArrow.svg";
import { ReactComponent as RightArrowIcon } from "logic/src/components/icons/rightArrow.svg";
import { ReactComponent as SlashIcon } from "logic/src/components/icons/slash.svg";
import { ReactComponent as ZoomIn } from "logic/src/components/icons/zoomIn.svg";
import { ReactComponent as ZoomOut } from "logic/src/components/icons/zoomOut.svg";
import Popup from "./components/Popup";
import PdfDocument from "./Document";
import { ISelectionDetail, ISelection } from "logic/src/objects/chat/types";
import { ICutBoxFull } from "./types";
import {
  selectText,
  gotoSelection,
  cutImg,
  highlightSelectedNodes,
  getTextWithoutChildren,
} from "./utils";
import { useCut } from "./useCut";
import { EFileQueryType } from "logic/src/objects/chat/types";
import { HighLightPopup } from "./components/Popup";

import styles from "./index.module.scss";

const formatter: NonNullable<SliderSingleProps["tooltip"]>["formatter"] = (
  value: number | undefined,
) => `${Math.floor((value || 1) * 100)}%`;

function getDistanceToParent(parent: HTMLElement, element: HTMLElement | null) {
  let distance = 0;
  while (element && element !== parent) {
    distance += element.offsetTop;
    element = element.offsetParent as HTMLElement;
  }
  return element === parent ? distance : null; // 返回 null 表示未找到指定的父元素
}

interface IProps {
  currentSelection?: ISelection;
  highlightList?: (ISelection & { msgId?: string })[];
  onQuery?: (v: EFileQueryType, selection: ISelection) => void;
  url: string;
  onCut?: (v: File) => void;
  onDeleteSelection?: (v: ISelection & { msgId?: string }) => void;
  onCloseFile?: () => void;
  hideActionBar?: boolean;
  readOnly?: boolean;
}

export default function PdfViewer({
  currentSelection,
  highlightList,
  onQuery,
  url,
  onCut,
  onDeleteSelection,
  onCloseFile,
  hideActionBar,
  readOnly,
}: IProps) {
  const [pageInfo, setPageInfo] = useState<{
    page: number;
    pageNumber: number;
  }>({ page: 1, pageNumber: 1 });
  const [scale, setScale] = useState<number>(1);
  const loadedRef = useRef<boolean>(false);
  const highlightListRef = useRef<ISelection[] | undefined>(highlightList);
  highlightListRef.current = highlightList;
  const [selection, setSelection] = useState<ISelectionDetail | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const docRef = useRef<any>();
  const docContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const size = useSize(containerRef);
  const handleCut = (v: ICutBoxFull) => {
    cutImg(v, onCut || (() => {}));
  };
  const { handler, startCut } = useCut({
    onCut: handleCut,
  });

  const gotoElement = useCallback((e: HTMLElement) => {
    if (scrollerRef.current && docContainerRef.current) {
      const top = getDistanceToParent(docContainerRef.current, e);
      scrollerRef.current.scrollTop =
        top !== null ? top : scrollerRef.current.scrollTop;
    }
  }, []);

  const scrollToPage = useCallback(
    (pageNumber: number | string) => {
      if (loadedRef.current) {
        if (Number(pageNumber)) {
          const pages = docRef.current.pages;
          const page = pages.current[Number(pageNumber) - 1];
          if (page) {
            gotoElement(page);
            return;
          }
        }
      }
    },
    [gotoElement],
  );

  const handleMouseUp = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const currentSelection = window.getSelection();
    if (selection) {
      setSelection(null);
      currentSelection?.removeAllRanges();
      return;
    }

    const selectedText = currentSelection?.toString();
    if (currentSelection && selectedText) {
      const range = currentSelection.getRangeAt(0);
      const lastRange =
        currentSelection.rangeCount > 1
          ? currentSelection.getRangeAt(currentSelection.rangeCount - 1)
          : range;
      const rect = range.getBoundingClientRect();
      const selectInfo = {
        id: v4(),
        text: selectedText,
        startPage: Number(range.startContainer.parentElement?.dataset.page),
        startId: range.startContainer.parentElement?.dataset.itemId || "",
        startOffset: range.startOffset,
        endPage: Number(lastRange.endContainer.parentElement?.dataset.page),
        endId: lastRange.endContainer.parentElement?.dataset.itemId || "",
        endOffset: lastRange.endOffset,
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
        mouseX: e.x,
        mouseY: e.y,
      };

      // 解决鼠标划到文字外面时, 找不到选区结束元素的问题
      if (!selectInfo.endId) {
        const selectionFrament = lastRange.cloneContents();
        const elements = selectionFrament.querySelectorAll(".custom-text");
        const lastCustomTextElement: HTMLElement = elements[
          elements.length - 1
        ] as HTMLElement;
        if (lastCustomTextElement) {
          const realLastElement = document.querySelector(
            `[data-item-id="${lastCustomTextElement.dataset.itemId}"]`,
          );
          if (realLastElement) {
            const realLastElementLen =
              getTextWithoutChildren(realLastElement).length;
            selectInfo.endPage = Number(lastCustomTextElement.dataset.page);
            selectInfo.endId = lastCustomTextElement.dataset.itemId || "";
            let endOffset =
              selectInfo.endId === selectInfo.startId
                ? selectInfo.startOffset +
                  lastCustomTextElement.innerText.length
                : lastCustomTextElement.innerText.length;
            if (endOffset > realLastElementLen) {
              endOffset = realLastElementLen;
            }

            selectInfo.endOffset = endOffset;
          }
        }
      }
      setSelection(selectInfo);
    } else {
      setSelection(null);
    }
  };

  useEffect(() => {
    if (docRef.current) {
      docContainerRef.current?.addEventListener("mouseup", handleMouseUp);

      return () =>
        docContainerRef.current?.removeEventListener("mouseup", handleMouseUp);
    }
  }, [docRef.current, selection]);

  useEffect(() => {
    if (currentSelection) {
      if (!highlightList?.find(item => item.id === currentSelection.id)) {
        selectText(currentSelection);
      }

      const textElement1 = document.querySelector(
        `[data-item-id="${currentSelection.startId}"]`,
      );

      if (textElement1) {
        gotoElement(textElement1 as HTMLElement);
      }
    }
  }, [currentSelection]);

  const highlight = useCallback(() => {
    if (highlightListRef.current) {
      highlightSelectedNodes(highlightListRef.current!);
    } else {
      highlightSelectedNodes([]);
    }
  }, []);

  useEffect(() => {
    highlight();
  }, [highlightList]);

  useEffect(() => {
    setTimeout(() => {
      highlight();
    }, 2000);
  }, [scale]);

  const handleLoadSuccess = useCallback((v: number) => {
    highlight();
    setPageInfo(pre => ({ ...pre, pageNumber: v }));
    scrollToPage(v);
    loadedRef.current = true;
    if (currentSelection) {
      selectText(currentSelection);
      const textElement1 = document.querySelector(
        `[data-item-id="${currentSelection.startId}"]`,
      );

      if (textElement1) {
        gotoElement(textElement1 as HTMLElement);
      }
    }
  }, []);

  const handleRemoveHighLight = (id: string) => {
    const selection = highlightList?.find(it => it.id === id);
    if (selection) {
      onDeleteSelection?.(selection);
    }
  };

  const handleHightLightClick = (type: EFileQueryType, id: string) => {
    const selection = highlightList?.find(it => it.id === id);
    if (selection) {
      onQuery?.(type, selection);
    }
  };

  const handleAddPage = () => {
    setPageInfo(pre => {
      let next = pre.page + 1;
      next = next > pre.pageNumber ? pre.pageNumber : next;
      scrollToPage(next);
      return { ...pre, page: next };
    });
  };

  const handleMinusPage = () => {
    setPageInfo(pre => {
      let next = pre.page - 1;
      next = next < 1 ? 1 : next;
      scrollToPage(next);
      return { ...pre, page: next };
    });
  };

  const { run: handleScroll } = useDebounceFn(
    () => {
      const scrollPositions = scrollerRef.current?.getClientRects();
      if (scrollPositions && scrollPositions[0]) {
        const scrollPosition = scrollPositions[0];
        const elements = document.elementsFromPoint(
          scrollPosition.x + scrollPosition.width / 2,
          scrollPosition.y + 100,
        ) as HTMLElement[];
        const pageElement = elements.find(item => item.dataset?.pageNumber);
        if (pageElement) {
          setPageInfo(pre => ({
            ...pre,
            page: Number(pageElement.dataset?.pageNumber),
          }));
        }
      }
    },
    { wait: 500 },
  );

  const zoomIn = () => {
    if (scale < 2) {
      setScale(scale + 0.1);
    }
  };

  const zoomOut = () => {
    if (scale > 0.5) {
      setScale(scale - 0.1);
    }
  };

  return (
    <div className={styles.container} ref={containerRef}>
      {!hideActionBar && (
        <div className={styles.header}>
          <CloseIcon className={styles.closeIcon} onClick={onCloseFile} />
          <RightArrowIcon
            onClick={handleAddPage}
            className={styles.arrowIcon}
          />
          <span className={styles.pageNumber}>{pageInfo.pageNumber}</span>
          <SlashIcon className={styles.slashIcon} />

          <InputNumber
            min={1}
            value={pageInfo.page}
            onChange={(v: number | null) => {
              if (v) {
                setPageInfo(pre => ({ ...pre, page: v }));
                scrollToPage(v);
              }
            }}
            max={pageInfo.pageNumber}
            size="small"
            controls={false}
            className={styles.input}
          />
          <LeftArrowIcon
            onClick={handleMinusPage}
            className={styles.arrowIcon}
          />
          {!readOnly ? (
            <CutIcon className={styles.cut} onClick={startCut} />
          ) : (
            <span className={styles.cut}></span>
          )}
          <ZoomIn className={styles.zoom} onClick={zoomIn} />
          <ConfigProvider
            theme={{
              token: {
                // colorPrimaryBorderHover: "#aaaaaa",
                /* 这里是你的全局 token */
              },
              components: {
                Slider: {
                  // // @ts-ignore
                  // railBg: "#aaaaaa",
                  // // @ts-ignore
                  // railHoverBg: "#999999",
                  // // @ts-ignore
                  // trackBg: "#aaaaaa",
                  // @ts-ignore
                  // trackHoverBg: "#999999",
                  handleActiveOutlineColor: "rgba(22, 119, 255, 0)",
                  handleActiveColor: "#999999",
                  handleColor: "#999999",
                  handleLineWidthHover: 2,
                  handleSize: 10,
                  handleSizeHover: 10,
                },
              },
            }}
          >
            <span className={styles.slider}>
              <Slider
                min={0.5}
                max={2}
                step={0.1}
                tooltip={{ formatter }}
                onChange={(v: number) => setScale(v)}
                value={scale}
              />
            </span>
          </ConfigProvider>

          <ZoomOut className={styles.zoom} onClick={zoomOut} />
        </div>
      )}

      <div className={styles.body}>
        <div
          className={styles.documentContainer}
          onScroll={handleScroll}
          ref={scrollerRef}
        >
          <PdfDocument
            onLoadSuccess={handleLoadSuccess}
            docContainerRef={docContainerRef}
            docRef={docRef}
            url={url}
            scale={scale}
          />
        </div>
        {handler}
      </div>
      <HighLightPopup
        onRemove={handleRemoveHighLight}
        onClick={handleHightLightClick}
      />
      {!readOnly && selection && (
        <Popup
          onClose={() => setSelection(null)}
          onClick={(type: EFileQueryType, v: ISelection) => {
            setSelection(null);
            onQuery?.(type, v);
          }}
          text="w"
          selection={selection}
        />
      )}
    </div>
  );
}
